import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { AuthField, useAuthErrorText } from "./components";

interface TotpStepProps {
  /** Verify the code; rejects with an AuthError. */
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
}

/** Step 2 of the login: the 6-digit app code (auto-submits) or a one-time backup code. */
export function TotpStep({ onSubmit, onBack }: TotpStepProps) {
  const { t } = useTranslation("auth");
  const errorText = useAuthErrorText();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (value: string) => {
    if (pending) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit(value);
    } catch (err: unknown) {
      setError(errorText(err));
      setCode("");
    } finally {
      setPending(false);
    }
  };

  const onFormSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (code.trim()) {
      void submit(code.trim());
    }
  };

  return (
    <form onSubmit={onFormSubmit} className="flex flex-col gap-4" noValidate data-step="totp">
      <p className="text-sm text-muted-foreground">
        {recovery ? t("totp.recovery_intro") : t("totp.intro")}
      </p>
      {recovery ? (
        <AuthField
          key="recovery"
          label={t("totp.recovery_code")}
          name="recovery"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus
          value={code}
          error={error}
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
      ) : (
        <AuthField
          key="totp"
          label={t("totp.code")}
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          autoFocus
          value={code}
          error={error}
          className="text-center font-mono text-2xl tracking-[0.4em]"
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(digits);
            if (digits.length === 6) {
              void submit(digits);
            }
          }}
        />
      )}
      <Button type="submit" size="lg" disabled={pending}>
        {t("totp.submit")}
      </Button>
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <button
          type="button"
          className="min-h-touch underline underline-offset-4"
          onClick={() => {
            setRecovery((r) => !r);
            setCode("");
            setError(null);
          }}
        >
          {recovery ? t("totp.use_app") : t("totp.use_recovery")}
        </button>
        <button type="button" className="min-h-touch underline underline-offset-4" onClick={onBack}>
          {t("totp.back")}
        </button>
      </div>
    </form>
  );
}
